import React, { useEffect, useMemo, useState } from 'react';

const generateRadialQuadrantChart = (originalData, labels, ch) => {
  return new Promise((resolve, reject) => {
    if (!originalData || !Array.isArray(originalData)) {
      reject(new Error('Invalid data format: data must be an array'));
      return;
    }

    if (typeof window !== 'undefined' && window.document) {
      const canvas = window.document.createElement('canvas');
      canvas.width = 500;
      canvas.height = 350;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error("Canvas context not found!");
        reject(new Error('Could not get canvas context'));
        return;
      }

      const starterLabels = ['mind', 'soul', 'strength', 'heart'];
      const leadershipLabels = ['strategic', 'visionary', 'performance', 'collaborative'];

      const isStarter = labels.every((label) => starterLabels.includes(label));
      const labelOrder = isStarter ? starterLabels : leadershipLabels;

      // Rearrange data according to labelOrder
      const orderedData = [];
      labelOrder.forEach((label) => {
        const index = labels.indexOf(label);
        if (index !== -1) {
          orderedData.push(originalData[index]);
        }
      });

      // Adjust for clockwise rendering
      const data = [orderedData[2], orderedData[3], orderedData[1], orderedData[0]];
      const adjustedLabels = [labelOrder[2], labelOrder[3], labelOrder[1], labelOrder[0]];

      const colors = ['#C99212', '#B6111F', '#00577D', '#667F96'];

      ctx.save();
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxRadius = 150;
      const whiteLineWidth = 10;

      // Nonlinear radius calculation: use sqrt for a sublinear increase
      const maxValue = Math.max(...data);
      const angles = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'white';
      ctx.lineWidth = whiteLineWidth;

      data.forEach((value, index) => {
        const minRadius = 55;

        // Nonlinear mapping: use square root
        let normalized = maxValue > 0 ? value / maxValue : 0;
        normalized = Math.max(0, Math.min(normalized, 1));
        const nonlinear = Math.sqrt(normalized);

        // Map nonlinear [0,1] to [minRadius, maxRadius]
        const radius = minRadius + (maxRadius - minRadius) * nonlinear;

        const startAngle = angles[index];
        const endAngle = startAngle + (Math.PI / 2);

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.lineTo(centerX, centerY);
        ctx.closePath();
        ctx.fillStyle = colors[index];
        ctx.fill();

        const midAngle = (startAngle + endAngle) / 2;
        // Use fixed offset from center instead of radius-based positioning
        const offsetX = 70; // Horizontal distance from center
        const offsetY = 50; // Vertical distance from center
        const textX = centerX + offsetX * Math.cos(midAngle);
        const textY = centerY + offsetY * Math.sin(midAngle);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '500 18px Helvetica';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label = `${(Number(value) || 0).toFixed(2)}%`;
        ctx.fillText(label, textX, textY);
      });

      // Draw uniform white separator lines from center to outer edge
      ctx.strokeStyle = 'white';
      ctx.lineWidth = whiteLineWidth;
      angles.forEach((angle) => {
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        const outerX = centerX + maxRadius * Math.cos(angle);
        const outerY = centerY + maxRadius * Math.sin(angle);
        ctx.lineTo(outerX, outerY);
        ctx.stroke();
      });

      ctx.fillStyle = '#343333';
      ctx.font = '400 22px Helvetica';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Draw corner labels based on adjusted order
      const cornerLabels = adjustedLabels.map(label =>
        label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
      );

      ctx.fillText(cornerLabels[3], centerX - maxRadius - 18, centerY - maxRadius);
      ctx.fillText(cornerLabels[2], centerX - maxRadius - 18, centerY + maxRadius);
      ctx.fillText(cornerLabels[0], centerX + maxRadius + 18, centerY - maxRadius);
      ctx.fillText(cornerLabels[1], centerX + maxRadius + 18, centerY + maxRadius);

      ctx.restore();
      resolve(canvas.toDataURL('image/png'));
    } else {
      reject(new Error('Window or document not available'));
    }
  });
};

const StarterChart360 = React.memo(({ originalData, labels, ch }) => {
  const [chartImage, setChartImage] = useState(null);
  const [error, setError] = useState(null);
  const dataSignature = useMemo(() => JSON.stringify({ data: originalData ?? [], labels: labels ?? [] }), [originalData, labels]);

  useEffect(() => {
    if (!originalData || !labels) {
      setError(new Error('No data or labels provided'));
      console.error("No data or labels provided");
      return;
    }

    generateRadialQuadrantChart(originalData, labels, ch)
      .then((img) => {
        setChartImage(img);
      })
      .catch((err) => {
        console.error("Chart generation error:", err);
        setError(err);
      });
  }, [dataSignature, ch]);

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
        <div>Error generating chart: {error.message}</div>
      </div>
    );
  }

  return (
    <div style={{
      width: '210px',
      maxWidth: '100%',
      maxHeight: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {chartImage && (
        <img
          src={chartImage}
          alt=''
          style={{
            width: 210,
            height: 130,
            display: 'flex',
            alignSelf: 'center',
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            padding: 0
          }}
        />
      )}
    </div>
  );
});

export default StarterChart360;
